import { subMonths } from 'date-fns';
import { z } from 'zod/v4';

import { getLatestSelectableDate } from '@/shared/utils/date';
import { CommonSearchParamsSchema } from '@/shared/utils/schemas';

export const mediaPlansSearchParamsSchema = CommonSearchParamsSchema.extend({});

export type MediaPlansSearchParams = z.infer<
  typeof mediaPlansSearchParamsSchema
>;

export const mediaPlanDetailParamsSchema = z.object({
  id: z.coerce.number().int().positive(),
});

const penetrationObjectivesSchema = z.object({
  dataMode: z.literal('PENETRATION'),
  secondaryGoal: z.enum(['VELOCITY', 'VOLUME']).nullable(),
});

const salesObjectivesSchema = z.object({
  dataMode: z.literal('SALES'),
  secondaryGoal: z.enum(['SHARE', 'VELOCITY']).nullable(),
});

export const mediaPlanObjectivesSchema = z.discriminatedUnion('dataMode', [
  penetrationObjectivesSchema,
  salesObjectivesSchema,
]);
export type MediaPlanObjectives = z.infer<typeof mediaPlanObjectivesSchema>;

export const mediaPlanDetailSearchParamsSchema = z
  .object({
    tab: z
      .enum(['insights-explorer', 'planning', 'activation'])
      .default('insights-explorer')
      .catch('insights-explorer'),
    act: z
      .enum(['associated', 'trafficking', 'diagnostics', 'experiment'])
      .default('associated')
      .catch('associated'),
    plan: z.enum(['overview', 'compare']).default('overview').catch('overview'),
    diag: z.enum(['perf', 'detail']).optional().catch(undefined),
    iex: z.enum(['vol', 'bdi']).optional().catch(undefined),
    campaignId: z.string().optional().catch(undefined),
    adGroups: z.array(z.string()).optional().catch(undefined),
    fromDate: z.coerce
      .date()
      .default(() => subMonths(getLatestSelectableDate(), 1))
      .catch(() => subMonths(getLatestSelectableDate(), 1)),
    toDate: z.coerce
      .date()
      .default(() => getLatestSelectableDate())
      .catch(() => getLatestSelectableDate()),
  })
  .and(
    mediaPlanObjectivesSchema.catch({
      dataMode: 'PENETRATION',
      secondaryGoal: null,
    })
  );

export type MediaPlanDetailTab = z.infer<
  typeof mediaPlanDetailSearchParamsSchema
>['tab'];

export type MediaPlanActivationTab = z.infer<
  typeof mediaPlanDetailSearchParamsSchema
>['act'];

export type MediaPlanPlanningTab = z.infer<
  typeof mediaPlanDetailSearchParamsSchema
>['plan'];

const baseMediaPlanSchema = z.object({
  name: z.string().min(1, 'Media plan name is required'),
  advertiserName: z.string().optional(),
  accountName: z.string().optional(),
  platform: z.string().min(1, 'Platform is required'),
  description: z.string().nullable(),
  audienceLibrary: z.number().nullable().optional(),
  audienceType: z.enum(['NONE', 'EXISTING']),
  associateExperiment: z.boolean().optional(),
});

export const mediaPlanFormSchema = z
  .discriminatedUnion('dataMode', [
    baseMediaPlanSchema.extend(penetrationObjectivesSchema.shape),
    baseMediaPlanSchema.extend(salesObjectivesSchema.shape),
  ])
  .check((ctx) => {
    if (
      ctx.value.audienceType === 'EXISTING' &&
      ctx.value.audienceLibrary == null
    ) {
      ctx.issues.push({
        code: 'custom',
        path: ['audienceLibrary'],
        message: 'Please select an audience',
        input: ctx.value.audienceLibrary,
      });
    }
  });

export type MediaPlanFormValues = z.infer<typeof mediaPlanFormSchema>;
